import { useRef } from "react";
import type { Pt } from "@tasduvar/engine";
import { toSvg, type Transform } from "./geometry";
import { snap } from "./snap";

/** Ekran pikseli → SVG birimi çarpanı (viewBox ölçeği); jsdom'da CTM yoktur, 1 kabul edilir. */
function pxScale(el: Element): number {
  const svg = el.closest("svg");
  const ctm = svg && "getScreenCTM" in svg ? (svg as SVGSVGElement).getScreenCTM?.() : null;
  return ctm && ctm.a ? 1 / ctm.a : 1;
}

/**
 * `ortho`: sürükleme baskın eksene kilitlenir (CAD ORTHO anahtarı, README §5.2).
 * `invalid`: hesap bu çokgenle başarısız — sahne son geçerli sonuçla kalır, güncel çizgi kırmızı çizilir ki kullanıcı düzeltebilsin.
 */
export function PolygonEditor({ t, points, onChange, ortho, invalid }: { t: Transform; points: Pt[]; onChange: (p: Pt[]) => void; ortho?: boolean; invalid?: boolean }): JSX.Element {
  const drag = useRef<{ i: number; startClient: { x: number; y: number }; start: Pt; k: number } | null>(null);
  const isBase = (p: Pt): boolean => Math.abs(p.y) < 1e-9;
  return (
    <g data-part="polygon-editor" data-invalid={invalid || undefined}>
      <polygon points={points.map((p) => { const s = toSvg(t, p); return `${s.x},${s.y}`; }).join(" ")} fill={invalid ? "#DC2626" : "none"} fillOpacity={0.12} stroke={invalid ? "#DC2626" : "#1F4E79"} strokeWidth={invalid ? 2 : 1} strokeDasharray={invalid ? "6 3" : undefined} pointerEvents="none" />
      {points.map((p, i) => {
        const q = points[(i + 1) % points.length]!;
        const a = toSvg(t, p), b = toSvg(t, q);
        return <line key={`e${i}`} data-edge={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="transparent" strokeWidth={10} style={{ cursor: "copy" }}
          onDoubleClick={() => { const next = [...points]; next.splice(i + 1, 0, { x: snap((p.x + q.x) / 2), y: snap((p.y + q.y) / 2) }); onChange(next); }} />;
      })}
      {points.map((p, i) => {
        const s = toSvg(t, p);
        return <circle key={`v${i}`} data-vertex={i} cx={s.x} cy={s.y} r={5} fill="#fff" stroke="#1F4E79" strokeWidth={2} style={{ cursor: isBase(p) ? "ew-resize" : "move", touchAction: "none" }}
          onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); drag.current = { i, startClient: { x: e.clientX, y: e.clientY }, start: p, k: pxScale(e.currentTarget) }; }}
          onPointerMove={(e) => { const d = drag.current; if (!d || d.i !== i) return;
            let dx = ((e.clientX - d.startClient.x) * d.k) / t.scale, dy = -((e.clientY - d.startClient.y) * d.k) / t.scale;
            if (ortho) { if (Math.abs(dx) >= Math.abs(dy)) dy = 0; else dx = 0; }
            const next = points.map((pt, k) => k !== i ? pt : { x: snap(d.start.x + dx), y: isBase(d.start) ? 0 : snap(d.start.y + dy) }); onChange(next); }}
          onPointerUp={() => { drag.current = null; }}
          onContextMenu={(e) => { e.preventDefault(); if (points.length > 3) onChange(points.filter((_, k) => k !== i)); }} />;
      })}
    </g>
  );
}
